import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useToast } from '@/hooks/use-toast';
import { triggerCalendarSync } from '@/lib/googleCalendarAutoSync';

export interface ParteDataBasic {
  id: string;
  numero_parte: string | null;
  fecha_trabajo: string;
  descripcion_trabajo: string;
  duracion_total: string | null;
  estado: string;
  firma_cliente: string | null;
  tecnico?: { nombre: string } | null;
}

export interface JornadaDataBasic {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string | null;
  horas_trabajadas: number | null;
  tipo_trabajo: string;
  descripcion_trabajo: string | null;
  tecnico?: { nombre: string } | null;
}

export interface TecnicoAsignado {
  tecnico_id: string;
  rol: 'principal' | 'apoyo';
  nombre?: string;
  avatar_url?: string | null;
}

export interface OrdenTrabajo {
  id: string;
  numero_ot: string;
  titulo: string;
  descripcion?: string;
  cliente_id: string;
  sede_id?: string;
  tecnico_asignado_id?: string;
  obra_id?: string;
  prioridad: 'baja' | 'media' | 'alta' | 'urgente';
  estado: 'creada' | 'asignada' | 'en_progreso' | 'finalizada' | 'cancelada';
  tipo_trabajo?: string;
  fecha_creacion?: string;
  fecha_asignacion?: string;
  fecha_limite?: string;
  hora_ejecucion?: string;
  duracion_estimada?: string;
  materiales_necesarios?: string;
  creada_por?: string;
  notas_admin?: string;
  created_at?: string;
  updated_at?: string;
  cliente_nombre?: string;
  cliente?: {
    id: string;
    nombre: string;
    direccion?: string;
    poblacion?: string;
    codigo_postal?: string;
    telefono?: string;
    email?: string;
    horario_apertura?: string;
    horario_cierre?: string;
    personas_contacto?: any[];
  };
  tecnico_asignado?: {
    user_id: string;
    nombre: string;
  };
  tecnicos_asignados?: TecnicoAsignado[];
  creador?: {
    user_id: string;
    nombre: string;
  };
  obra?: {
    id: string;
    nombre: string;
  };
  partes_count?: number;
  partes_data?: {
    borrador: number;
    finalizado: number;
    facturado: number;
  };
  partes_details?: ParteDataBasic[];
  jornadas_count?: number;
  jornadas_details?: JornadaDataBasic[];
}

interface CalendarioFilters {
  tecnicoId: string;
  estado: string;
  prioridad: string;
  clienteId: string;
}

interface OrdenesTrabajoContextType {
  ordenes: OrdenTrabajo[];
  loading: boolean;
  createOrden: (orden: Omit<OrdenTrabajo, 'id' | 'numero_ot' | 'fecha_creacion' | 'created_at' | 'updated_at'>, tecnicosAsignados?: TecnicoAsignado[]) => Promise<OrdenTrabajo>;
  updateOrden: (id: string, updates: Partial<OrdenTrabajo>) => Promise<void>;
  deleteOrden: (id: string) => Promise<void>;
  assignTecnico: (id: string, tecnicoId: string | null) => Promise<void>;
  assignTecnicos: (ordenId: string, tecnicos: TecnicoAsignado[]) => Promise<void>;
  assignMultipleToTecnico: (ordenIds: string[], tecnicoId: string) => Promise<void>;
  assignMultipleToTecnicos: (ordenIds: string[], tecnicos: TecnicoAsignado[]) => Promise<void>;
  getOrdenById: (id: string) => OrdenTrabajo | undefined;
  getOrdenesByTecnico: (tecnicoId: string) => OrdenTrabajo[];
  getOrdenesAsignadasUsuario: () => OrdenTrabajo[];
  getOrdenesDisponiblesParaParte: () => OrdenTrabajo[];
  getClientesAsignados: () => { id: string; nombre: string }[];
  getPartesByOrdenId: (ordenId: string) => any[];
  getFilteredOrdenesCalendario: (filters: CalendarioFilters) => OrdenTrabajo[];
  getTecnicosWithOrdenes: () => Array<{ user_id: string; nombre: string; count: number }>;
  refreshOrdenes: () => Promise<void>;
}

const OrdenesTrabajoContext = createContext<OrdenesTrabajoContextType | undefined>(undefined);

export const useOrdenesTrabajoContext = () => {
  const context = useContext(OrdenesTrabajoContext);
  if (!context) {
    throw new Error('useOrdenesTrabajoContext must be used within an OrdenesTrabajoProvider');
  }
  return context;
};

export const OrdenesTrabajoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ordenes, setOrdenes] = useState<OrdenTrabajo[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, perfil } = useAuth();
  const { toast } = useToast();

  console.log('OrdenesTrabajoProvider: Initializing with user:', user?.id, 'perfil:', perfil?.rol, 'loading:', loading);

  const loadOrdenes = async () => {
    if (!user) {
      console.log('OrdenesTrabajoProvider: No user, skipping load');
      return;
    }
    
    console.log('OrdenesTrabajoProvider: Starting to load ordenes for user:', user.id, 'rol:', perfil?.rol);
    
    try {
      setLoading(true);
      console.log('OrdenesTrabajoProvider: Building query...');
      
      let query = supabase
        .from('ordenes_trabajo')
        .select(`
          *,
          cliente:clientes!inner(id, nombre, direccion, poblacion, codigo_postal, telefono, email, horario_apertura, horario_cierre, personas_contacto, activo),
          tecnico_asignado:perfiles!ordenes_trabajo_tecnico_asignado_id_fkey(user_id, nombre),
          creador:perfiles!ordenes_trabajo_creada_por_fkey(user_id, nombre),
          obra:obras(id, nombre)
        `)
        .eq('cliente.activo', true)
        .order('created_at', { ascending: false });

      // If user is technician, only show assigned orders
      if (perfil?.rol === 'tecnico') {
        console.log('OrdenesTrabajoProvider: User is tecnico, filtering by assignment');
        query = query.eq('tecnico_asignado_id', user.id);
      }

      console.log('OrdenesTrabajoProvider: Executing main query...');
      const { data, error } = await query;

      if (error) {
        console.error('OrdenesTrabajoProvider: Main query error:', error);
        throw error;
      }
      
      console.log('OrdenesTrabajoProvider: Main query successful, loaded', data?.length || 0, 'ordenes');
      console.log('OrdenesTrabajoProvider: Enriching with partes data...');
      
      // Enrich with partes count, jornadas count, details, and tecnicos_asignados
      const ordenesWithDocumentation = await Promise.all(
        (data || []).map(async (orden) => {
          // Fetch partes with details
          const { data: partesData, error: partesError } = await supabase
            .from('partes_trabajo')
            .select(`
              id,
              numero_parte,
              fecha_trabajo,
              descripcion_trabajo,
              duracion_total,
              estado,
              firma_cliente,
              tecnico:perfiles!fk_partes_trabajo_tecnico(nombre)
            `)
            .eq('orden_trabajo_id', orden.id)
            .order('fecha_trabajo', { ascending: false });

          // Fetch jornadas with details
          const { data: jornadasData, error: jornadasError } = await supabase
            .from('obra_jornadas')
            .select(`
              id,
              fecha,
              hora_inicio,
              hora_fin,
              horas_trabajadas,
              tipo_trabajo,
              descripcion_trabajo,
              tecnico:perfiles!fk_obra_jornadas_tecnico_id(nombre)
            `)
            .eq('orden_trabajo_id', orden.id)
            .order('fecha', { ascending: false });

          // Fetch tecnicos asignados
          const { data: tecnicosData, error: tecnicosError } = await supabase
            .from('orden_trabajo_tecnicos')
            .select(`
              tecnico_id,
              rol,
              perfil:perfiles!orden_trabajo_tecnicos_tecnico_id_fkey(nombre, avatar_url)
            `)
            .eq('orden_trabajo_id', orden.id);

          if (partesError) {
            console.error('OrdenesTrabajoProvider: Error loading partes for orden:', orden.id, partesError);
          }
          if (jornadasError) {
            console.error('OrdenesTrabajoProvider: Error loading jornadas for orden:', orden.id, jornadasError);
          }
          if (tecnicosError) {
            console.error('OrdenesTrabajoProvider: Error loading tecnicos for orden:', orden.id, tecnicosError);
          }

          const partesByEstado = (partesData || []).reduce((acc, parte) => {
            acc[parte.estado] = (acc[parte.estado] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          const tecnicos_asignados: TecnicoAsignado[] = (tecnicosData || []).map((t: any) => ({
            tecnico_id: t.tecnico_id,
            rol: t.rol as 'principal' | 'apoyo',
            nombre: t.perfil?.nombre,
            avatar_url: t.perfil?.avatar_url,
          }));

          return {
            ...orden,
            tecnicos_asignados,
            partes_count: partesData?.length || 0,
            partes_data: {
              borrador: partesByEstado.borrador || 0,
              finalizado: partesByEstado.finalizado || 0,
              facturado: partesByEstado.facturado || 0,
            },
            partes_details: partesData || [],
            jornadas_count: jornadasData?.length || 0,
            jornadas_details: jornadasData || [],
          };
        })
      );
      
      console.log('OrdenesTrabajoProvider: Enrichment completed, setting ordenes');
      setOrdenes(ordenesWithDocumentation as OrdenTrabajo[]);
    } catch (error) {
      console.error('OrdenesTrabajoProvider: Error loading órdenes de trabajo:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las órdenes de trabajo",
        variant: "destructive",
      });
    } finally {
      console.log('OrdenesTrabajoProvider: Load completed, setting loading to false');
      setLoading(false);
    }
  };

  const createOrden = async (orden: Omit<OrdenTrabajo, 'id' | 'numero_ot' | 'fecha_creacion' | 'created_at' | 'updated_at'>, tecnicosAsignados?: TecnicoAsignado[]) => {
    try {
      // Remove readonly/computed fields for insert
      const { 
        cliente, 
        tecnico_asignado, 
        tecnicos_asignados,
        creador, 
        partes_count, 
        partes_data,
        ...baseData 
      } = orden as any;
      
      // Build insert data with required fields
      const insertData: Record<string, any> = {
        titulo: baseData.titulo,
        cliente_id: baseData.cliente_id,
        creada_por: user?.id!,
        numero_ot: '', // Will be auto-generated by trigger
        empresa_id: perfil?.empresa_id, // Required for RLS policy
      };

      // Add optional fields if they have values
      if (baseData.descripcion) insertData.descripcion = baseData.descripcion;
      if (baseData.prioridad) insertData.prioridad = baseData.prioridad;
      if (baseData.estado) insertData.estado = baseData.estado;
      if (baseData.tecnico_asignado_id) insertData.tecnico_asignado_id = baseData.tecnico_asignado_id;
      if (baseData.fecha_limite) insertData.fecha_limite = baseData.fecha_limite;
      if (baseData.hora_ejecucion) insertData.hora_ejecucion = baseData.hora_ejecucion;
      if (baseData.duracion_estimada) insertData.duracion_estimada = baseData.duracion_estimada;
      if (baseData.notas_admin) insertData.notas_admin = baseData.notas_admin;
      if (baseData.tipo_trabajo) insertData.tipo_trabajo = baseData.tipo_trabajo;
      if (baseData.sede_id) insertData.sede_id = baseData.sede_id;
      
      // Add obra_id if provided
      if ((orden as any).obra_id) insertData.obra_id = (orden as any).obra_id;
      
      const { data, error } = await supabase
        .from('ordenes_trabajo')
        .insert([insertData as any])
        .select(`
          *,
          cliente:clientes(id, nombre, direccion, poblacion, codigo_postal, telefono, email, horario_apertura, horario_cierre, personas_contacto),
          tecnico_asignado:perfiles!ordenes_trabajo_tecnico_asignado_id_fkey(user_id, nombre),
          creador:perfiles!ordenes_trabajo_creada_por_fkey(user_id, nombre),
          obra:obras(id, nombre)
        `)
        .single();

      if (error) throw error;

      // If tecnicos were provided, insert them into orden_trabajo_tecnicos
      if (tecnicosAsignados && tecnicosAsignados.length > 0 && data) {
        const tecnicosInsert = tecnicosAsignados.map(t => ({
          orden_trabajo_id: data.id,
          tecnico_id: t.tecnico_id,
          rol: t.rol,
        }));

        const { error: tecnicosError } = await supabase
          .from('orden_trabajo_tecnicos')
          .insert(tecnicosInsert);

        if (tecnicosError) {
          console.error('Error assigning tecnicos:', tecnicosError);
        }
      }
      
      setOrdenes(prev => [{ ...data, tecnicos_asignados: tecnicosAsignados || [] } as OrdenTrabajo, ...prev]);
      toast({
        title: "Éxito",
        description: "Orden de trabajo creada correctamente",
      });
      
      return data as OrdenTrabajo;
    } catch (error) {
      console.error('Error creating orden:', error);
      toast({
        title: "Error",
        description: "No se pudo crear la orden de trabajo",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateOrden = async (id: string, updates: Partial<OrdenTrabajo>) => {
    try {
      // Whitelist only valid database columns to prevent Supabase errors
      const validColumns = [
        'titulo', 'descripcion', 'cliente_id', 'tecnico_asignado_id',
        'prioridad', 'estado', 'tipo_trabajo', 'fecha_limite',
        'hora_ejecucion', 'duracion_estimada', 'materiales_necesarios',
        'notas_admin', 'obra_id', 'fecha_asignacion'
      ];
      const sanitizedUpdates: Record<string, any> = {};
      for (const key of validColumns) {
        if (key in updates) {
          const val = (updates as any)[key];
          sanitizedUpdates[key] = val === undefined ? null : val;
        }
      }

      const { data, error } = await supabase
        .from('ordenes_trabajo')
        .update(sanitizedUpdates as any)
        .eq('id', id)
        .select(`
          *,
          cliente:clientes(id, nombre, direccion, poblacion, codigo_postal, telefono, email, horario_apertura, horario_cierre, personas_contacto),
          tecnico_asignado:perfiles!ordenes_trabajo_tecnico_asignado_id_fkey(user_id, nombre),
          creador:perfiles!ordenes_trabajo_creada_por_fkey(user_id, nombre),
          obra:obras(id, nombre)
        `)
        .single();

      if (error) throw error;

      // Sincronización silenciosa con Google Calendar si cambia algo relevante
      const camposCalendario = [
        'fecha_limite', 'hora_ejecucion', 'duracion_estimada', 'estado', 'tecnico_asignado_id',
      ];
      if (camposCalendario.some(k => k in sanitizedUpdates)) {
        const previa = ordenes.find(o => o.id === id);
        triggerCalendarSync([
          previa?.tecnico_asignado_id,
          (data as any)?.tecnico_asignado_id,
          ...(previa?.tecnicos_asignados?.map(t => t.tecnico_id) ?? []),
        ]);
      }

      setOrdenes(prev => prev.map(o => o.id === id ? ({ ...o, ...(data as any) } as OrdenTrabajo) : o));
      toast({
        title: "Éxito",
        description: "Orden de trabajo actualizada correctamente",
      });
    } catch (error) {
      console.error('Error updating orden:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la orden de trabajo",
        variant: "destructive",
      });
      throw error;
    }
  };

  const assignTecnico = async (id: string, tecnicoId: string | null) => {
    try {
      if (tecnicoId === null) {
        // Unassign: remove all tecnicos from orden_trabajo_tecnicos
        await supabase
          .from('orden_trabajo_tecnicos')
          .delete()
          .eq('orden_trabajo_id', id);
        
        await updateOrden(id, { tecnico_asignado_id: null, estado: 'creada' as any });
      } else {
        // First, remove existing and add new
        await supabase
          .from('orden_trabajo_tecnicos')
          .delete()
          .eq('orden_trabajo_id', id);

        await supabase
          .from('orden_trabajo_tecnicos')
          .insert({ orden_trabajo_id: id, tecnico_id: tecnicoId, rol: 'principal' });

        await updateOrden(id, { tecnico_asignado_id: tecnicoId });
      }
      await loadOrdenes();
    } catch (error) {
      throw error;
    }
  };

  const assignTecnicos = async (ordenId: string, tecnicos: TecnicoAsignado[]) => {
    try {
      const { error: delError } = await supabase
        .from('orden_trabajo_tecnicos')
        .delete()
        .eq('orden_trabajo_id', ordenId);
      if (delError) throw delError;

      if (tecnicos.length > 0) {
        const tecnicosInsert = tecnicos.map(t => ({
          orden_trabajo_id: ordenId,
          tecnico_id: t.tecnico_id,
          rol: t.rol,
        }));
        const { error } = await supabase
          .from('orden_trabajo_tecnicos')
          .insert(tecnicosInsert);
        if (error) throw error;
      } else {
        // No technicians left → clear primary explicitly
        await supabase
          .from('ordenes_trabajo')
          .update({ tecnico_asignado_id: null, updated_at: new Date().toISOString() })
          .eq('id', ordenId);
      }

      // Update local state without reloading the whole context — prevents cascade
      // re-renders that crash open dialogs (React error #185).
      const principal = tecnicos.find(t => t.rol === 'principal')?.tecnico_id || null;
      const previos = ordenes.find(o => o.id === ordenId);
      triggerCalendarSync([
        ...(previos?.tecnicos_asignados?.map(t => t.tecnico_id) ?? []),
        previos?.tecnico_asignado_id,
        ...tecnicos.map(t => t.tecnico_id),
      ]);
      setOrdenes(prev => prev.map(o =>
        o.id === ordenId
          ? { ...o, tecnicos_asignados: tecnicos, tecnico_asignado_id: principal }
          : o
      ));
    } catch (error) {
      console.error('Error assigning tecnicos:', error);
      toast({
        title: "Error",
        description: "No se pudieron asignar los técnicos",
        variant: "destructive",
      });
      throw error;
    }
  };

  const assignMultipleToTecnicos = async (ordenIds: string[], tecnicos: TecnicoAsignado[]) => {
    try {
      const afectados: (string | undefined)[] = tecnicos.map(t => t.tecnico_id);
      for (const ordenId of ordenIds) {
        const previa = ordenes.find(o => o.id === ordenId);
        afectados.push(previa?.tecnico_asignado_id, ...(previa?.tecnicos_asignados?.map(t => t.tecnico_id) ?? []));
        const { error: delError } = await supabase
          .from('orden_trabajo_tecnicos')
          .delete()
          .eq('orden_trabajo_id', ordenId);
        if (delError) throw delError;

        if (tecnicos.length > 0) {
          const insertRows = tecnicos.map(t => ({
            orden_trabajo_id: ordenId,
            tecnico_id: t.tecnico_id,
            rol: t.rol,
          }));
          const { error } = await supabase
            .from('orden_trabajo_tecnicos')
            .insert(insertRows);
          if (error) throw error;
        }
      }
      triggerCalendarSync(afectados);
      await loadOrdenes();
      toast({
        title: "Éxito",
        description: `${ordenIds.length} órdenes asignadas correctamente`,
      });
    } catch (error) {
      console.error('Error assigning multiple ordenes (multi-tecnico):', error);
      toast({
        title: "Error",
        description: "No se pudieron asignar las órdenes",
        variant: "destructive",
      });
      throw error;
    }
  };

  const assignMultipleToTecnico = async (ordenIds: string[], tecnicoId: string) => {
    try {
      const afectados: (string | undefined)[] = [tecnicoId];
      // For each orden, delete existing and insert new
      for (const ordenId of ordenIds) {
        const previa = ordenes.find(o => o.id === ordenId);
        afectados.push(previa?.tecnico_asignado_id, ...(previa?.tecnicos_asignados?.map(t => t.tecnico_id) ?? []));
        await supabase
          .from('orden_trabajo_tecnicos')
          .delete()
          .eq('orden_trabajo_id', ordenId);

        await supabase
          .from('orden_trabajo_tecnicos')
          .insert({ orden_trabajo_id: ordenId, tecnico_id: tecnicoId, rol: 'principal' });
      }

      triggerCalendarSync(afectados);

      // Refresh to get updated data
      await loadOrdenes();
      
      toast({
        title: "Éxito",
        description: `${ordenIds.length} órdenes asignadas correctamente`,
      });
    } catch (error) {
      console.error('Error assigning multiple ordenes:', error);
      toast({
        title: "Error",
        description: "No se pudieron asignar las órdenes",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteOrden = async (id: string) => {
    try {
      const previa = ordenes.find(o => o.id === id);
      const { error } = await supabase
        .from('ordenes_trabajo')
        .delete()
        .eq('id', id);

      if (error) throw error;

      triggerCalendarSync([
        previa?.tecnico_asignado_id,
        ...(previa?.tecnicos_asignados?.map(t => t.tecnico_id) ?? []),
      ]);

      setOrdenes(prev => prev.filter(o => o.id !== id));
      toast({
        title: "Éxito",
        description: "Orden de trabajo eliminada correctamente",
      });
    } catch (error) {
      console.error('Error deleting orden:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la orden de trabajo",
        variant: "destructive",
      });
      throw error;
    }
  };

  const getOrdenById = (id: string): OrdenTrabajo | undefined => {
    return ordenes.find(o => o.id === id);
  };

  const getOrdenesByTecnico = (tecnicoId: string): OrdenTrabajo[] => {
    return ordenes.filter(o => 
      o.tecnico_asignado_id === tecnicoId ||
      o.tecnicos_asignados?.some(t => t.tecnico_id === tecnicoId)
    );
  };

  const getOrdenesAsignadasUsuario = (): OrdenTrabajo[] => {
    if (!user?.id) return [];
    return ordenes.filter(o => 
      (o.tecnico_asignado_id === user.id || o.tecnicos_asignados?.some(t => t.tecnico_id === user.id)) && 
      ['asignada', 'en_progreso'].includes(o.estado)
    );
  };

  const getOrdenesDisponiblesParaParte = (): OrdenTrabajo[] => {
    if (!user?.id) return [];
    
    // Admins and gestores can see all OTs in their company that are not finalized
    if (perfil?.rol === 'admin' || perfil?.rol === 'gestor') {
      return ordenes.filter(o => 
        ['creada', 'asignada', 'en_progreso'].includes(o.estado)
      );
    }
    
    // Technicians can only see OTs assigned to them (via tecnico_asignado_id or tecnicos_asignados)
    return ordenes.filter(o => 
      (o.tecnico_asignado_id === user.id || o.tecnicos_asignados?.some(t => t.tecnico_id === user.id)) && 
      ['creada', 'asignada', 'en_progreso'].includes(o.estado)
    );
  };

  const getClientesAsignados = (): { id: string; nombre: string }[] => {
    if (!user?.id) return [];
    const clientesIds = new Set<string>();
    const clientesUnicos: { id: string; nombre: string }[] = [];
    
    ordenes
      .filter(o => o.tecnico_asignado_id === user.id || o.tecnicos_asignados?.some(t => t.tecnico_id === user.id))
      .forEach(orden => {
        if (orden.cliente && !clientesIds.has(orden.cliente.id)) {
          clientesIds.add(orden.cliente.id);
          clientesUnicos.push({
            id: orden.cliente.id,
            nombre: orden.cliente.nombre
          });
        }
      });
    
    return clientesUnicos;
  };

  const getPartesByOrdenId = (ordenId: string): any[] => {
    // This will be populated when we fetch partes data
    // For now, return empty array as the data is already included in ordenes
    return [];
  };

  const getFilteredOrdenesCalendario = (filters: CalendarioFilters): OrdenTrabajo[] => {
    let filtered = ordenes;

    // Técnico filter - check both tecnico_asignado_id and tecnicos_asignados
    if (filters.tecnicoId && filters.tecnicoId !== 'all') {
      filtered = filtered.filter(orden => 
        orden.tecnico_asignado_id === filters.tecnicoId ||
        orden.tecnicos_asignados?.some(t => t.tecnico_id === filters.tecnicoId)
      );
    }

    // Estado filter
    if (filters.estado && filters.estado !== 'all') {
      filtered = filtered.filter(orden => orden.estado === filters.estado);
    }

    // Prioridad filter
    if (filters.prioridad && filters.prioridad !== 'all') {
      filtered = filtered.filter(orden => orden.prioridad === filters.prioridad);
    }

    // Cliente filter
    if (filters.clienteId && filters.clienteId !== 'all') {
      filtered = filtered.filter(orden => orden.cliente_id === filters.clienteId);
    }

    return filtered;
  };

  const getTecnicosWithOrdenes = (): Array<{ user_id: string; nombre: string; count: number }> => {
    const tecnicoCount = new Map<string, { nombre: string; count: number }>();
    
    ordenes.forEach(orden => {
      if (orden.tecnico_asignado_id && orden.tecnico_asignado) {
        const key = orden.tecnico_asignado_id;
        const existing = tecnicoCount.get(key);
        tecnicoCount.set(key, {
          nombre: orden.tecnico_asignado.nombre,
          count: (existing?.count || 0) + 1
        });
      }
    });

    return Array.from(tecnicoCount.entries()).map(([user_id, data]) => ({
      user_id,
      nombre: data.nombre,
      count: data.count
    }));
  };

  const refreshOrdenes = async () => {
    await loadOrdenes();
  };

  useEffect(() => {
    if (user && perfil) {
      loadOrdenes();
    }
  }, [user, perfil]);

  const value: OrdenesTrabajoContextType = {
    ordenes,
    loading,
    createOrden,
    updateOrden,
    deleteOrden,
    assignTecnico,
    assignTecnicos,
    assignMultipleToTecnico,
    assignMultipleToTecnicos,
    getOrdenById,
    getOrdenesByTecnico,
    getOrdenesAsignadasUsuario,
    getOrdenesDisponiblesParaParte,
    getClientesAsignados,
    getPartesByOrdenId,
    getFilteredOrdenesCalendario,
    getTecnicosWithOrdenes,
    refreshOrdenes,
  };

  return (
    <OrdenesTrabajoContext.Provider value={value}>
      {children}
    </OrdenesTrabajoContext.Provider>
  );
};